import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useCarDriversApi } from "../../http/carDrivers/api";
import { UpsertCarDriver } from "../../http/carDrivers/types";
import ModalDialog from "../ui/ModalDialog";
import CarDriverForm, { CarDriverFormBody } from "./CarDriverForm";

type EditCarDriverDialogProps = {
  carDriverId: string;
  onClose: (success?: boolean) => void;
  carId?: string;
};

const EditCarDriverDialog: FC<EditCarDriverDialogProps> = ({
  carDriverId,
  onClose,
  carId,
}: EditCarDriverDialogProps) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const carDriversApi = useCarDriversApi();

  const carDriverSwr = carDriversApi.getAdminCarDriver(carDriverId, aborter.current.signal);
  const [carDriver, isInitialized] = useDataInitializer(carDriverSwr.data, carDriverSwr.error);

  useEffect(() => {
    carDriverSwr.error && dispatch(showErrorNotification(i18n, carDriverSwr.error));
  }, [carDriverSwr.error]);

  useEffect(() => {
    carDriverId && carDriverSwr.trigger();
  }, [carDriverId]);

  const onSubmit = async (formData: CarDriverFormBody): Promise<void> => {
    if (carDriver) {
      const body: UpsertCarDriver = {
        carId: carId ?? formData.car.id,
        driverId: formData.driver.id,
        startDate: formData.startDate,
        endDate: formData.endDate,
        typeOfUse: formData.typeOfUse.value,
        currentVersionNumber: carDriver?.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await carDriversApi.updateAdminCarDriver(carDriver.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.carDrivers.edit}>
      <CarDriverForm
        onSubmit={onSubmit}
        isDisabled={!carDriver?.id || !userSettings.permissions.carDrivers.update || isSubmitting}
        isLoading={!isInitialized}
        carDriver={carDriver}
        carId={carId}
      />
    </ModalDialog>
  );
};

export default EditCarDriverDialog;
